import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useCallback, useState, useRef, useEffect } from 'react'
import React from 'react'
import ReactPlayer from 'react-player'

import styles from './kdt-ugm-5th.module.scss'

interface IUgmCurriculumProps {
  curriculumDetailList: {
    id: number
    step: string
    title: string
    desc: string
    url: string
    contents: {
      contentsIntro?: string
      contentsInner?: string[]
    }[]
  }[]
}

export const UgmCurriculumM = ({ curriculumDetailList }: IUgmCurriculumProps) => {
  const [openItems, setOpenItems] = useState<{ [key: number]: boolean }>({
    0: true,
  })
  const [isFirstTabInView, setIsFirstTabInView] = useState(false)
  const containerRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (openItems[0]) {
          setIsFirstTabInView(entry.isIntersecting)
        }
      },
      {
        threshold: 0.5,
      },
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current)
      }
    }
  }, [openItems])

  const onOpenHandler = useCallback((id: number) => {
    setOpenItems(prevState => ({
      ...prevState,
      [id]: !prevState[id],
    }))
    if (id === 0) {
      setIsFirstTabInView(true)
    }
  }, [])

  return (
    <ul className="flex flex-col gap-3" ref={containerRef}>
      {curriculumDetailList.map(detail => {
        const isOpen = !!openItems[detail.id]
        const shouldAutoplay = detail.id === 0 ? isFirstTabInView : isOpen

        return (
          <li
            key={detail.id}
            className="group list-none overflow-hidden"
            onClick={() => onOpenHandler(detail.id)}
          >
            <div className="cursor-pointer list-none group-open:pb-1">
              <div className="flex flex-row items-center gap-5">
                <div
                  className={`${
                    isOpen ? 'rounded-t-lg' : 'rounded-lg'
                  } flex w-full items-center justify-between gap-x-2 bg-[#383838] px-4 py-3`}
                >
                  <div className="flex flex-col gap-2">
                    <p className="text-xs font-bold text-yellow-450">{detail.step}</p>
                    <p
                      className={`${isOpen ? 'text-white' : 'text-label-alternative'} text-base font-bold`}
                    >
                      {detail.title}
                    </p>
                  </div>
                  <ChevronDownIcon
                    className={`${
                      isOpen ? 'rotate-180 transform text-white' : 'text-label-alternative'
                    } ml-auto mr-2.5 h-auto w-4 shrink-0 grow-0`}
                  />
                </div>
              </div>
            </div>

            {/* Open 상태일 때 내용 보여주기 */}
            {isOpen && (
              <div className="flex w-full">
                <div className="flex-1 rounded-b-lg bg-[#383838] px-4 pb-4">
                  <div className="mb-3">
                    <p className="mb-3 text-sm leading-normal text-white">{detail.desc}</p>
                    <div
                      className="w-full overflow-hidden rounded"
                      style={{
                        position: 'relative',
                        paddingTop: '49.88%',
                      }}
                    >
                      <ReactPlayer
                        url={detail.url}
                        width="100%"
                        height="100%"
                        controls={false}
                        muted={true}
                        playing={shouldAutoplay}
                        playsInLine
                        playsinline={true}
                        loop
                        config={{
                          file: {
                            attributes: {
                              autoPlay: shouldAutoplay,
                              muted: true,
                              playsInline: true,
                              controls: false,
                            },
                          },
                        }}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                        }}
                        alt="-"
                      />
                    </div>
                  </div>
                  {detail.contents.map(content => (
                    <ul
                      key={content.contentsIntro || 'content'}
                      className={`${styles.curriculum_accordion_ul} flex flex-col rounded bg-[#2F2F2F] px-4 py-3`}
                    >
                      {content.contentsIntro && (
                        <li className="mb-3 text-sm leading-normal text-white only-of-type:mb-0">
                          {content.contentsIntro}
                        </li>
                      )}
                      {content.contentsInner &&
                        content.contentsInner.map(el => (
                          <li
                            key={el}
                            className="border-t-[0.5px] border-[#4E4E4E] py-2 last:pb-0 first-of-type:border-t-0 first-of-type:pt-0 last-of-type:pb-0"
                          >
                            <p
                              className={`${styles.text_dot} ${styles.text_dot_section7} whitespace-pre-line text-sm leading-normal text-label-assistive`}
                            >
                              {el}
                            </p>
                          </li>
                        ))}
                    </ul>
                  ))}
                </div>
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
